import { CommonModule, LowerCasePipe, NgClass } from '@angular/common';
import { Component, computed, effect, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonRow,
  LoadingController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  SkeletonCardGenericComponent,
  SkeletonCardTwoColumnsComponent,
  SpinnerLoadingComponent,
} from '@okcargo-apps/layout-ionic';
import { MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import {
  EFreightForShipperStatus,
  EStopStatus,
  EStopType,
  getEmptyFreightBasicForShipper,
  getEmptyFreightDetailForShipper,
  ICoords,
  IFreightBasicForShipper,
  IFreightDetailedForShipper,
  transformToIFreightBasicFromIFreightDetailForShipper,
  UUID,
} from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { DateFnsModule } from 'ngx-date-fns';
import { DownloadDocumentService } from '../../download-document/download-document.service';
import { FreightShipperService } from '../freight-shipper.service';

@Component({
  selector: 'lib-freight-shipper-detail-main',
  templateUrl: './freight-shipper-detail-main.component.html',
  styleUrls: ['./freight-shipper-detail-main.component.scss'],
  imports: [
    CommonModule,
    IonItemDivider,
    IonItemGroup,
    IonLabel,
    IonList,
    IonItem,
    IonCol,
    IonRow,
    IonGrid,
    IonButton,
    IonIcon,
    NgClass,
    DateFnsModule,
    LowerCasePipe,
    TranslateModule,
    SkeletonCardTwoColumnsComponent,
    SpinnerLoadingComponent,
    SkeletonCardGenericComponent,
    MessageUiIonicComponent,
  ],
})
export class FreightShipperDetailMainComponent {
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly freightShipService = inject(FreightShipperService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly platformService = inject(PlatformService);

  @Input()
  id!: string;

  @Output()
  detailTitle = new EventEmitter<string>();

  public readonly EStopType = EStopType;
  public readonly EStopStatus = EStopStatus;
  public readonly EFreightStatus = EFreightForShipperStatus;
  public readonly ECapabilities = ECapabilities;

  public freight: IFreightDetailedForShipper = getEmptyFreightDetailForShipper();
  public freightBasic: IFreightBasicForShipper = getEmptyFreightBasicForShipper();

  public messages = computed(() => this.messagesUiService.getMessagesForObjectId(this.id));

  constructor() {
    effect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.freightShipService.lastRefreshFreightShipDetailTimestamp;
      console.log('GO TO loadFreightDetail() => with id ', this.id);
      this.loadFreightDetail();
    });
  }

  private loadFreightDetail(): void {
    this.freightShipService.findFreightShipDetail$(this.id).subscribe((freight) => {
      this.freight = freight;
      console.log('loadFreightDetail() freight', this.freight);
      this.freightBasic = transformToIFreightBasicFromIFreightDetailForShipper(freight);
      this.sendTitleToParentComponent(this.translate.instant('general.freight') + ' ' + this.freight.title);
    });
  }

  private sendTitleToParentComponent(value: string): void {
    this.detailTitle.emit(value);
  }

  public goToDetailStop(stopId: UUID): void {
    const element = document.getElementById(stopId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public openMap(coords: ICoords): void {
    const lat = coords.latitude;
    const lng = coords.longitude;

    let url = `https://www.google.com/maps?q=${lat},${lng}`;
    if (this.platformService.isAndroid) {
      url = `geo:${lat},${lng}?q=${lat},${lng}`;
    } else if (this.platformService.isIos) {
      url = `maps://?q=${lat},${lng}`;
    }

    window.open(url, '_blank');
  }

  public async downloadDocument(url: string | undefined, filename: string | undefined): Promise<void> {
    if (url) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.downloading_file'),
      });
      loading.present();

      await this.downloadDocumentService.downloadFile(url, filename);

      await loading.dismiss();
    }
  }
}
